import { CATEGORY_KEY_TO_OFFICIAL, CATEGORY_KEY_ALIASES } from '@/utils/edtechCategoryNames';

/*
 * [i18n] Display-time translation for the English UI labels this module emits.
 * The constants and return values below stay ENGLISH on purpose: several are compared, sorted,
 * used as map keys or sent to the API, and other pages read them too. A component that renders
 * one passes its `t` (from useTranslation) to mktLabel, which swaps a known English label for its
 * translation key at render time. Anything not listed (API labels, vendor or account names)
 * passes through unchanged. Never translate at module top level — it would freeze one language.
 */
const MKT_LABEL_KEYS = {
  Displacement: 'mktDisplacement',
  'Renewal open': 'mktRenewalOpen',
  Greenfield: 'mktGreenfield',
  'Replacing in-house': 'mktReplacingInHouse',
  Expansion: 'mktExpansion',
  'Existing customer': 'mktExistingCustomer',
  'Adjacent vendor': 'mktAdjacentVendor',
  'Not an opportunity': 'mktNotAnOpportunity',
  'Market opportunity': 'mktMarketOpportunity',
  'Estimated value': 'mktEstimatedValue',
  'Renewal window': 'mktRenewalWindow',
  Recency: 'mktRecency',
  Momentum: 'momentum',
  'Restored to accounts': 'mktRestoredToAccounts',
  'Added to accounts': 'mktAddedToAccounts',
  "That account isn't in your list.": 'mktRemoveSkipNotInAccounts',
  "This account wasn't added from Market. Use Disengage instead.": 'mktRemoveSkipNotFromMarket',
  'Could not remove account.': 'mktCouldNotRemoveAccount',
  'Every institution we hold a record for in the countries you cover. Nothing has been filtered out yet.': 'mktTipKnown',
  'The ones from that list you could realistically sell to. If this matches Total market, no exclusion rules have been set up yet, so nothing is being filtered.': 'mktTipAddressable',
  'Accounts where we found something to work with: either the vendor they use looks like it could lose the account, or nobody has bought anything and there is a real need. It does not mean a deal is open.': 'mktTipInMarket',
  'The accounts from your market that also score high enough on fit to be worth starting with. Start here.': 'mktTipPriority',
  'How much of this market anyone has actually looked at. A low number means the research has not been done, not that the market is empty.': 'mktTipExamined',
  'Out of 100. How well the institution fits what you sell, combined with what is happening there right now, then held back if we are working from thin evidence.': 'mktTipScore',
  'What kind of deal this would be. Hover any badge for what it means.': 'mktTipType',
  'Who they use for this category today. Institutions usually run different vendors in different categories, so this is the one that matters for this deal, not the only one they have.': 'mktTipIncumbent',
  'Purchase momentum over the last 4 and 12 weeks. Negative scores indicate unfavourable signals; positive scores indicate favourable signals.': 'mktTipMomentum',
  'How much of the usual background we hold on this account, judged against similar institutions rather than against a perfect record. Where it is thin we hold the score back instead of guessing upward. This is about how sure we are, not how good the account is.': 'mktTipEvidence',
  'How solid the case is that their current vendor could lose the account. The strongest is a complaint written into the minutes of a governing body. The weakest is pain we have inferred from their workflow, where nobody has actually complained.': 'mktTipGrade',
  'How much room this institution has to fund something new, compared with others of its size and type. It is not a read on what they spend on EdTech.': 'mktTipFinancial',
  'How far this institution has taken AI in teaching and assessment, from 1 (just exploring) to 5 (built into how they work).': 'mktTipAiReadiness',
  'How deeply their current vendor is embedded: across the whole institution, several faculties, one department, or a trial. The wider it goes the bigger the deal, and the harder they are to unseat.': 'mktTipContractScope',
  'We look at two things, how mature their policy is and how deeply they have adopted tools, and take the higher of the two. Levels 4 and 5 need both to check out; a policy on its own stops at 3. Anything we have not seen confirmed in 18 months drops a level.': 'mktAiScale',
  'Exploring. Nothing published and no sign of anyone using AI tools yet.': 'mktAiDef1',
  'Piloting. Something is running, with no policy around it.': 'mktAiDef2',
  'Policy in place. Published and approved, but we cannot see it being used.': 'mktAiDef3',
  'Governed adoption. Both the policy and real usage are evidenced.': 'mktAiDef4',
  'Embedded. AI is part of how they teach and assess.': 'mktAiDef5',
  Exploring: 'mktAiExploring',
  Piloting: 'mktAiPiloting',
  'Policy in place': 'mktAiPolicyInPlace',
  'Governed adoption': 'mktAiGovernedAdoption',
  Embedded: 'mktAiEmbedded',
  Explicit: 'mktGradeExplicit',
  Reported: 'mktGradeReported',
  Documented: 'mktGradeDocumented',
  Timing: 'mktGradeTiming',
  Implied: 'mktGradeImplied',
  'A governing body, senate, committee or students\' union put the complaint on the record.': 'mktGradeExplicitHint',
  'The complaint is published somewhere, but not in a meeting where decisions get made.': 'mktGradeReportedHint',
  'The institution has published a specific failing in the vendor\'s product, usually in an accessibility statement.': 'mktGradeDocumentedHint',
  'Their contract runs out inside your renewal window, so it goes back out to tender whatever they think of the vendor.': 'mktGradeTimingHint',
  'We found pain in the workflow this vendor owns, but nobody has said they want to leave.': 'mktGradeImpliedHint',
  Priority: 'mktPriority',
  'Worth a look': 'mktWorthALook',
  'Rest of market': 'mktRestOfMarket',
  'Adjacent market': 'mktAdjacentMarket',
  'In your market': 'mktInYourMarket',
  'Your best openings right now - greenfield, displacement or expansion.': 'mktQueueIntroPriority',
  'Real openings at institutions that fit you less well. Worth working once you have been through Priority.': 'mktQueueIntroInMarket',
  'The rest of the market you could sell into. Something is missing before you can judge them. Best fits are at the top.': 'mktQueueIntroRest',
  'Shows vendors already present that you don’t compete with, helping you understand the existing tech stack and where your solution could fit alongside it.': 'mktQueueIntroAdjacent',
  'Unknown vendor': 'mktUnknownVendor',
  'Confirmed risk': 'mktRiskConfirmed',
  Watch: 'mktRiskWatch',
  'Not yet checked': 'mktRiskNotYetChecked',
  'Looked · nothing found': 'mktRiskLookedNothingFound',
  'They have said they are unhappy with us, or their contract ends inside the horizon.': 'mktRiskConfirmedMeans',
  'There is a published failing in our product here, or a competitor has moved into a neighbouring category.': 'mktRiskWatchMeans',
  'Nobody has looked at this account. It is not the same as it being quiet.': 'mktRiskUnknownMeans',
  'We looked and found nothing. That is a real answer, not a gap.': 'mktRiskQuietMeans',
  'User Selected': 'mktUserSelected',
  Update: 'mktUpdate',
  Relationship: 'mktRelationship',
  'Sends this account to the research team to fill in the gaps: who they use, how widely, when the contract ends, and whether they are happy with it. Your score will not change today. When the research comes back the account is rescored and you will see it in What changed.': 'mktEnrichTooltip',
  'We’ll process your request within 48 hours and notify you when it’s ready.': 'mktEnrichRequestToast',
  'The share of institutions in your market running each vendor for this category. It is a count of institutions, not revenue. Institution shares can sum above 100% because an account can run more than one vendor.': 'mktPlayersChartTooltip',
  Other: 'signalFamilyLabel_default',
  'This account is in your Accounts, but Market had no score for it.': 'mktFitAddUnscoredFallback',
  All: 'all',
  Competitor: 'mktCompetitor',
  Adjacent: 'mktAdjacent',
  Ours: 'mktOurs',
  'In-house': 'mktInHouse',
  'Total market': 'mktTotalMarket',
  Addressable: 'mktAddressable',
  Fit: 'fit',
  Signals: 'signals',
  Confidence: 'confidence',
  'Rare event': 'mktRareEvent',
  'A rare qualifying event lifted the live activity axis on this score.': 'mktRareEventMeans',
  'Nobody has established this yet. It does not mean the deployment is small, so we leave it out of the score rather than marking it down.': 'mktContractScopeUnknownHint',
  'Buying signals': 'mktBuyingSignals',
  'How well an institution suits what you sell: what they use today and how replaceable it is, their size, and how ready they look to change. Turn it up to favour the right-shaped accounts even when nothing is happening there yet.': 'mktRubricFitHint',
  'Signs they are actually moving: a stated problem with what they use now, plus procurement, budget, a renewal coming up or a new award. Turn it up to lead with what is happening rather than who looks right on paper.': 'mktRubricSignalsHint',
  Strong: 'strong',
  Moderate: 'moderate',
  Limited: 'mktLimited',
  High: 'high',
  Low: 'low',
  'In play': 'mktInPlay',
  Candidates: 'mktCandidates',
  'Existing / expansion': 'mktExistingExpansion',
  'Greenfield / adjacent': 'mktGreenfieldAdjacent',
  // incumbent_state_label badges the API sends (fixed set, see marketRoutes.ts vendor_state switch)
  'LMS-native': 'mktStateLmsNative',
  'Suite module': 'mktStateSuiteModule',
  'Built in-house': 'mktStateBuiltInHouse',
  'Open source': 'mktStateOpenSource',
  'Nothing in place': 'mktStateNothingInPlace',
};

/** Translate a label this module produced, at render time. Unknown text is returned as-is. */
export function mktLabel(t, text) {
  if (text == null || typeof t !== 'function') return text;
  const key = Object.prototype.hasOwnProperty.call(MKT_LABEL_KEYS, text) ? MKT_LABEL_KEYS[text] : null;
  return key ? t(key) : text;
}

/** Opportunity type labels — do not invent synonyms. */
export const OPPORTUNITY_TYPE_META = {
  commercial_displacement: {
    key: 'commercial_displacement',
    label: 'Displacement',
    inPlay: true,
    quiet: false,
    hideFromTable: false,
  },
  renewal_recompete: {
    key: 'renewal_recompete',
    label: 'Renewal open',
    inPlay: true,
    quiet: false,
    hideFromTable: false,
  },
  displacement_candidate: {
    key: 'displacement_candidate',
    label: 'Displacement',
    inPlay: false,
    quiet: true,
    hideFromTable: true,
  },
  greenfield: {
    key: 'greenfield',
    label: 'Greenfield',
    inPlay: false,
    quiet: false,
    hideFromTable: false,
  },
  homegrown_displacement: {
    key: 'homegrown_displacement',
    label: 'Replacing in-house',
    inPlay: false,
    quiet: false,
    hideFromTable: false,
  },
  expansion: {
    key: 'expansion',
    label: 'Expansion',
    inPlay: false,
    quiet: false,
    hideFromTable: false,
  },
  addon: {
    key: 'addon',
    label: 'Existing customer',
    inPlay: false,
    quiet: false,
    hideFromTable: true,
  },
  coexistence_adoption: {
    key: 'coexistence_adoption',
    label: 'Adjacent vendor',
    inPlay: false,
    quiet: false,
    hideFromTable: false,
  },
  monitor: {
    key: 'monitor',
    label: 'Not an opportunity',
    inPlay: false,
    quiet: true,
    hideFromTable: true,
  },
};

export const TABLE_TYPE_FILTERS = Object.values(OPPORTUNITY_TYPE_META)
  .filter((m) => !m.hideFromTable)
  .map((m) => ({ value: m.key, label: m.label }));

export const DISPLACEMENT_TYPE_KEYS = [
  'commercial_displacement',
  'displacement_candidate',
  'homegrown_displacement',
];

export function isDisplacementTypeGroup(type) {
  return type === 'commercial_displacement';
}

export const SORT_OPTIONS = [
  { value: 'score', label: 'Market opportunity' },
  { value: 'value', label: 'Estimated value' },
  { value: 'renewal', label: 'Renewal window' },
  { value: 'recency', label: 'Recency' },
  { value: 'momentum', label: 'Momentum' },
];

export function canonicalizeOpportunityType(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (OPPORTUNITY_TYPE_META[s]) return s;
  const n = s
    .toLowerCase()
    .replace(/[·•]/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (
    n.includes('timing unknown')
    || n.includes('competitor present')
    || n.includes('competitor unknown')
    || n === 'candidate'
    || n === 'displacement candidate'
  ) {
    return 'displacement_candidate';
  }
  if (n === 'displacement' || n.includes('commercial displacement')) return 'commercial_displacement';
  if (n.includes('renewal')) return 'renewal_recompete';
  if (n.includes('greenfield') || n.includes('open field')) return 'greenfield';
  if (n.includes('expansion')) return 'expansion';
  if (n.includes('existing customer') || n === 'addon') return 'addon';
  if (n.includes('adjacent') || n.includes('coexistence')) return 'coexistence_adoption';
  if (n.includes('in-house') || n.includes('homegrown')) return 'homegrown_displacement';
  if (n.includes('monitor') || n.includes('not an opportunity')) return 'monitor';
  return s;
}

export function resolveOpportunityType(rowOrKey) {
  if (rowOrKey == null || rowOrKey === '') return '';
  if (typeof rowOrKey !== 'object') return canonicalizeOpportunityType(rowOrKey);
  const direct = canonicalizeOpportunityType(
    rowOrKey.opportunity_type
      || rowOrKey.type
      || rowOrKey.opportunity_type_label
      || rowOrKey.type_label,
  );
  if (direct) return direct;
  const fromCat = asList(rowOrKey.categories)
    .map((c) => canonicalizeOpportunityType(typeof c === 'string' ? null : c?.opportunity_type))
    .find(Boolean);
  if (fromCat) return fromCat;
  if (rowOrKey.queue === 'adjacent') return 'coexistence_adoption';
  if (rowOrKey.displacement_grade && rowOrKey.incumbent_vendor) return 'displacement_candidate';
  return '';
}

export function getOpportunityTypeMeta(type) {
  const key = canonicalizeOpportunityType(type);
  return OPPORTUNITY_TYPE_META[key] || {
    key: key || type,
    label: key ? String(key).replace(/_/g, ' ') : '—',
    inPlay: false,
    quiet: true,
    hideFromTable: false,
  };
}

export function opportunityTypeBadgeClass(type) {
  const resolved = canonicalizeOpportunityType(type);
  const key = resolved === 'displacement_candidate' ? 'commercial_displacement' : resolved;
  // Avoid solid #00C2C2 — reserved for primary actions (e.g. Add to Accounts).
  const classes = {
    commercial_displacement: 'bg-[#0A1A2F] text-white border-0 hover:bg-[#0A1A2F]',
    renewal_recompete: 'bg-[#9A3412] text-white border-0 hover:bg-[#9A3412]',
    homegrown_displacement: 'bg-[#1C485C] text-white border-0 hover:bg-[#1C485C]',
    greenfield: 'bg-[#E8EDF2] text-[#0A1A2F] border border-[#0A1A2F]/15 hover:bg-[#E8EDF2]',
    expansion: 'bg-[#4338CA] text-white border-0 hover:bg-[#4338CA]',
    coexistence_adoption: 'bg-[#64748B] text-white border-0 hover:bg-[#64748B]',
    addon: 'bg-[#059669] text-white border-0 hover:bg-[#059669]',
    monitor: 'bg-gray-100 text-[#5C6B7A] border-0 hover:bg-gray-100',
  };
  return `${classes[key] || 'bg-gray-100 text-[#5C6B7A] border-0 hover:bg-gray-100'} whitespace-nowrap`;
}

export function formatFinancialCapacity(row) {
  const raw = row?.financial_capacity
    ?? row?.financial_band
    ?? row?.financial_capacity_band
    ?? row?.capacity_band
    ?? row?.financial_situation
    ?? (row?.financial && (row.financial.capacity || row.financial.band || row.financial.label));
  if (raw != null && raw !== '') {
    if (typeof raw === 'object') {
      const label = raw.label || raw.band || raw.capacity || raw.value;
      if (label) return String(label).replace(/_/g, ' ');
    } else {
      return String(raw).replace(/_/g, ' ');
    }
  }
  const fromDrivers = inferFinancialFromDrivers(row);
  return fromDrivers || '—';
}

export function formatAiReadiness(row) {
  const raw = row?.ai_readiness_score ?? row?.ai_readiness;
  if (raw != null && raw !== '') {
    if (typeof raw === 'object') {
      const label = raw.label || raw.level || raw.score || raw.value;
      if (label != null && label !== '') return String(label);
    } else {
      return String(raw);
    }
  }
  const fromDrivers = inferAiFromDrivers(row);
  return fromDrivers || '—';
}

function driverByFactor(row, factors) {
  const list = Array.isArray(row?.drivers) ? row.drivers : [];
  const set = new Set(factors.map((f) => String(f).toLowerCase()));
  return list.find((d) => set.has(String(d?.factor || '').toLowerCase())) || null;
}

function inferFinancialFromDrivers(row) {
  const d = driverByFactor(row, ['f5_commercial', 'commercial', 'commercial_readiness']);
  const text = `${d?.detail || ''} ${d?.label || ''} ${row?.score_headline || ''}`.toLowerCase();
  if (!text.trim()) return null;
  if (text.includes('high financial') || text.includes('high headroom')) return 'High';
  if (text.includes('moderate financial') || text.includes('moderate headroom')) return 'Moderate';
  if (text.includes('low financial') || text.includes('low headroom')) return 'Low';
  return null;
}

function inferAiFromDrivers(row) {
  const d = driverByFactor(row, ['f4_readiness', 'change_readiness', 'ai_readiness']);
  const text = `${d?.detail || ''} ${d?.label || ''}`;
  const m = text.match(/level\s*(\d)\s*of\s*5/i);
  if (m) return m[1];
  return null;
}

function inferIncumbentVendor(row) {
  const direct = row?.incumbent?.vendor || row?.incumbent_vendor || row?.adjacent_vendor || row?.vendor;
  if (direct) return direct;
  const d = driverByFactor(row, ['f2_alignment', 'product_alignment', 'alignment']);
  const detail = d?.detail || '';
  // "Explorance in course evaluation, Instructure in lms"
  const m = detail.match(/^([^,]+?)\s+in\s+[a-z_ ]+/i);
  if (m) return m[1].trim();
  const headline = row?.score_headline || '';
  const hm = headline.match(/^[^,]*,\s*([^,]+?)\s+in\s+/i);
  if (hm) return hm[1].trim();
  return null;
}

export function isDisplacementType(typeOrRow) {
  const key = typeof typeOrRow === 'object' ? resolveOpportunityType(typeOrRow) : canonicalizeOpportunityType(typeOrRow);
  return DISPLACEMENT_TYPE_KEYS.includes(key);
}

export function looksLikeAccountRecordId(value) {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

/** Account page URLs use AccountFinal.id (UUID), not the market account_id (e.g. UKUNI-030). */
export function extractAccountPageId(...sources) {
  for (const src of sources) {
    if (looksLikeAccountRecordId(src)) return src.trim();
    if (!src || typeof src !== 'object') continue;
    const nested = [src, src.account, src.row, src.record, src.data, src.added_account];
    if (Array.isArray(src.added)) nested.push(...src.added);
    for (const obj of nested) {
      if (!obj || typeof obj !== 'object') continue;
      for (const key of [
        'tenant_account_uuid',
        'account_record_id',
        'account_uuid',
        'record_id',
        'uuid',
        'id',
      ]) {
        const v = obj[key];
        if (looksLikeAccountRecordId(v)) return String(v).trim();
      }
    }
  }
  return null;
}

/** First entry from POST /accounts/add `added[]`. */
export function extractAddedAccount(payload) {
  const list = Array.isArray(payload?.added) ? payload.added : [];
  return list[0] || null;
}

/** POST /accounts/add returns revived:true when a previously removed row is restored. */
export function isMarketAddRevived(payload) {
  if (payload?.revived === true) return true;
  const added = extractAddedAccount(payload);
  if (added?.revived === true) return true;
  const list = Array.isArray(payload?.added) ? payload.added : [];
  return list.some((a) => a?.revived === true);
}

export function marketAddSuccessTitle(payload) {
  return isMarketAddRevived(payload) ? 'Restored to accounts' : 'Added to accounts';
}

export const REMOVE_ACCOUNT_SKIP_MESSAGES = {
  already_removed: null,
  not_in_accounts: "That account isn't in your list.",
  not_added_from_market: "This account wasn't added from Market. Use Disengage instead.",
};

export function isRemovedFromListAccount(account) {
  if (!account || account.is_visible !== false) return false;
  return String(account.is_visible_reason || '') === 'removed_from_list';
}

/** Treat already_removed as success; surface other skip reasons. */
export function interpretRemoveAccountResult(data) {
  const removed = Array.isArray(data?.removed) ? data.removed : [];
  const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
  const blocking = skipped.filter((s) => s?.reason && s.reason !== 'already_removed');
  if (blocking.length) {
    const reason = String(blocking[0].reason);
    return {
      ok: false,
      reason,
      message: REMOVE_ACCOUNT_SKIP_MESSAGES[reason] || 'Could not remove account.',
    };
  }
  return { ok: true, restorable: data?.restorable !== false, removed, data };
}

/**
 * Account page link target — AccountFinal UUID only.
 * Prefer `tenant_account_uuid` from /opportunities; never route on tenant_account_id / UKUNI-*.
 */
export function marketAccountPageId(row) {
  if (!row || typeof row !== 'object') return null;
  if (looksLikeAccountRecordId(row.tenant_account_uuid)) {
    return String(row.tenant_account_uuid).trim();
  }
  return extractAccountPageId(row);
}

/**
 * A Market account exists twice: the pooled row and this tenant's own copy.
 * Only the tenant's carries a fit score, and /entities admits the pooled
 * partition for _V2 tenants, so the pooled row can come back first.
 */
export function pickTenantMarketAccountRow(accounts, { tenantId, poolAccountId } = {}) {
  const candidates = (accounts || []).filter((a) => extractAccountPageId(a));
  const prefix = tenantId ? `${tenantId}_` : '';
  const hit = candidates.find((a) =>
    (prefix && String(a?.account_id || '').startsWith(prefix))
    || (poolAccountId && a?.global_pool_account_id && a.global_pool_account_id === poolAccountId),
  );
  return hit || candidates[0] || null;
}

/** Brandfetch / Clearbit placeholders look valid but are not institution logos. */
export function isUntrustedAccountLogoUrl(url) {
  if (!url || typeof url !== 'string') return true;
  const s = url.toLowerCase();
  return (
    s.includes('brandfetch')
    || s.includes('logo.clearbit.com')
    || s.includes('clearbit.com/logo')
  );
}

/** Initials for a neutral avatar when logo_url is missing or untrusted. */
export function accountNameInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

/** Normalize market / tenant account codes for matching AccountFinal rows. */
export function normalizeMarketAccountCode(value, tenantId) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (tenantId && s.startsWith(`${tenantId}_`)) return s.slice(tenantId.length + 1);
  const m = s.match(/(UKUNI-\d+|AUUNI-\d+|USUNI-\d+|[A-Z]{2,}UNI-\d+)$/i);
  if (m) return m[1];
  return s;
}

/**
 * Build a lookup of AccountFinal records keyed by id, account_id, and normalized codes.
 */
export function indexTenantAccounts(accounts, tenantId) {
  const byKey = {};
  (accounts || []).forEach((a) => {
    if (!a || typeof a !== 'object') return;
    const keys = new Set();
    if (a.id) keys.add(String(a.id));
    if (a.account_id) {
      keys.add(String(a.account_id));
      const code = normalizeMarketAccountCode(a.account_id, tenantId);
      if (code) keys.add(code);
    }
    if (a.account_name) keys.add(`name:${String(a.account_name).trim().toLowerCase()}`);
    keys.forEach((k) => { byKey[k] = a; });
  });
  return byKey;
}

/** Resolve AccountFinal UUID for a market opportunity row via an accounts index. */
export function resolveAccountRecordIdFromIndex(row, byKey, tenantId) {
  const direct = extractAccountPageId(row);
  if (direct) return direct;
  if (!row || !byKey) return null;
  const candidates = [
    row.account_record_id,
    row.id,
    row.account_id,
    row.tenant_account_id,
    normalizeMarketAccountCode(row.account_id, tenantId),
    normalizeMarketAccountCode(row.tenant_account_id, tenantId),
    row.account_name ? `name:${String(row.account_name).trim().toLowerCase()}` : null,
  ].filter(Boolean).map(String);
  for (const key of candidates) {
    const acc = byKey[key];
    const id = extractAccountPageId(acc);
    if (id) return id;
  }
  return null;
}

export function isMarketAccountAdded(row, payload) {
  const src = { ...(payload || {}), ...(row || {}) };
  if (src.in_accounts === true || src.already_added === true || src.is_added === true || src.added === true) {
    return true;
  }
  if (src.in_accounts === false) return false;
  if (src.account_fit_score != null && src.account_fit_score !== '') return true;
  if (src.account_tier) return true;
  if (src.account_fit_label) return true;
  return false;
}

/** A trigger fired — the 20 in-play rows, not displacement_candidate. */
export function rowHasTrigger(row) {
  const triggers = asList(row?.triggers);
  return triggers.length > 0;
}

export function isLiveSignalRow(row) {
  if (row?.opportunity_type === 'monitor') return false;
  if (row?.opportunity_type === 'displacement_candidate') return false;
  return rowHasTrigger(row);
}

/** @deprecated use isLiveSignalRow */
export function isInPlayRow(row) {
  return isLiveSignalRow(row);
}

export const HEADLINE_TOOLTIPS = {
  known: 'Every institution we hold a record for in the countries you cover. Nothing has been filtered out yet.',
  addressable: 'The ones from that list you could realistically sell to. If this matches Total market, no exclusion rules have been set up yet, so nothing is being filtered.',
  in_market: 'Accounts where we found something to work with: either the vendor they use looks like it could lose the account, or nobody has bought anything and there is a real need. It does not mean a deal is open.',
  priority: 'The accounts from your market that also score high enough on fit to be worth starting with. Start here.',
  examined: 'How much of this market anyone has actually looked at. A low number means the research has not been done, not that the market is empty.',
};

export const COLUMN_TOOLTIPS = {
  score: 'Out of 100. How well the institution fits what you sell, combined with what is happening there right now, then held back if we are working from thin evidence.',
  type: 'What kind of deal this would be. Hover any badge for what it means.',
  incumbent: 'Who they use for this category today. Institutions usually run different vendors in different categories, so this is the one that matters for this deal, not the only one they have.',
  momentum: 'Purchase momentum over the last 4 and 12 weeks. Negative scores indicate unfavourable signals; positive scores indicate favourable signals.',
  evidence: 'How much of the usual background we hold on this account, judged against similar institutions rather than against a perfect record. Where it is thin we hold the score back instead of guessing upward. This is about how sure we are, not how good the account is.',
  grade: 'How solid the case is that their current vendor could lose the account. The strongest is a complaint written into the minutes of a governing body. The weakest is pain we have inferred from their workflow, where nobody has actually complained.',
  financial: 'How much room this institution has to fund something new, compared with others of its size and type. It is not a read on what they spend on EdTech.',
  ai_readiness: 'How far this institution has taken AI in teaching and assessment, from 1 (just exploring) to 5 (built into how they work).',
  contract_scope: 'How deeply their current vendor is embedded: across the whole institution, several faculties, one department, or a trial. The wider it goes the bigger the deal, and the harder they are to unseat.',
};

/** AI readiness 1–5 — matches /composition ai_readiness.definitions. */
export const AI_READINESS_DEFINITIONS = {
  scale: 'We look at two things, how mature their policy is and how deeply they have adopted tools, and take the higher of the two. Levels 4 and 5 need both to check out; a policy on its own stops at 3. Anything we have not seen confirmed in 18 months drops a level.',
  1: 'Exploring. Nothing published and no sign of anyone using AI tools yet.',
  2: 'Piloting. Something is running, with no policy around it.',
  3: 'Policy in place. Published and approved, but we cannot see it being used.',
  4: 'Governed adoption. Both the policy and real usage are evidenced.',
  5: 'Embedded. AI is part of how they teach and assess.',
};

export const AI_READINESS_LABELS = {
  1: 'Exploring',
  2: 'Piloting',
  3: 'Policy in place',
  4: 'Governed adoption',
  5: 'Embedded',
};

export function aiReadinessBadgeLabel(ai) {
  if (!ai) return null;
  if (ai.label) return String(ai.label);
  const level = Number(ai.level);
  if (AI_READINESS_LABELS[level]) return AI_READINESS_LABELS[level];
  return null;
}

export const DISPLACEMENT_GRADE_META = {
  explicit: {
    key: 'explicit',
    label: 'Explicit',
    hint: 'A governing body, senate, committee or students\' union put the complaint on the record.',
    className: 'bg-[#0A1A2F] text-white',
  },
  reported: {
    key: 'reported',
    label: 'Reported',
    hint: 'The complaint is published somewhere, but not in a meeting where decisions get made.',
    className: 'bg-[#334155] text-white',
  },
  documented: {
    key: 'documented',
    label: 'Documented',
    hint: 'The institution has published a specific failing in the vendor\'s product, usually in an accessibility statement.',
    className: 'bg-[#5C6B7A] text-white',
  },
  timing: {
    key: 'timing',
    label: 'Timing',
    hint: 'Their contract runs out inside your renewal window, so it goes back out to tender whatever they think of the vendor.',
    className: 'bg-[#1C485C] text-white',
  },
  implied: {
    key: 'implied',
    label: 'Implied',
    hint: 'We found pain in the workflow this vendor owns, but nobody has said they want to leave.',
    className: 'bg-[#E8EDF2] text-[#0A1A2F] border border-[#0A1A2F]/15',
  },
};

export function getDisplacementGradeMeta(grade) {
  if (!grade) return null;
  return DISPLACEMENT_GRADE_META[grade] || { key: grade, label: String(grade), className: 'bg-gray-100 text-gray-700', hint: '' };
}

/** Vendor, category and relationship travel together. */
export function getIncumbent(row) {
  const obj = row?.incumbent;
  const stateLabel = row?.incumbent_state_label
    || obj?.state_label
    || obj?.incumbent_state_label
    || null;
  if (obj && typeof obj === 'object' && (obj.vendor || obj.category || obj.relationship || stateLabel)) {
    return {
      vendor: obj.vendor || inferIncumbentVendor(row) || null,
      category: obj.category || null,
      relationship: obj.relationship || row?.vendor_relationship || null,
      displacement_grade: obj.displacement_grade || row?.displacement_grade || null,
      contract_scope: obj.contract_scope || row?.contract_scope || null,
      state_label: stateLabel || null,
      vendor_state: obj.vendor_state || null,
    };
  }
  // [2026-10-26] "Nothing in place" has no incumbent. The API sends `incumbent: null` on purpose for a
  // no_solution row, and inferring a name from the alignment driver put another category's footprint
  // ("Microsoft and Unitu" — student success) beside "Nothing in place" under hardship funding.
  const nothingInPlace = row?.incumbent_state_label === 'Nothing in place' || row?.vendor_state === 'no_solution';
  return {
    vendor: nothingInPlace ? null : (inferIncumbentVendor(row) || row?.incumbent_vendor || null),
    category: row?.incumbent_category || row?.category || null,
    relationship: row?.vendor_relationship || null,
    displacement_grade: row?.displacement_grade || null,
    contract_scope: row?.contract_scope || null,
    state_label: stateLabel || null,
    vendor_state: null,
  };
}

export const EVIDENCE_TOOLTIP =
  'How much of the usual background we hold on this account, judged against similar institutions rather than against a perfect record. Where it is thin we hold the score back instead of guessing upward. This is about how sure we are, not how good the account is.';

export const QUEUE_META = {
  priority: { key: 'priority', label: 'Priority', worklist: false },
  in_market: { key: 'in_market', label: 'Worth a look', worklist: false },
  rest: { key: 'rest', label: 'Rest of market', worklist: false },
  adjacent: { key: 'adjacent', label: 'Adjacent market', worklist: false },
};

export const QUEUE_INTROS = {
  priority: 'Your best openings right now - greenfield, displacement or expansion.',
  in_market: 'Real openings at institutions that fit you less well. Worth working once you have been through Priority.',
  rest: 'The rest of the market you could sell into. Something is missing before you can judge them. Best fits are at the top.',
  adjacent: 'Shows vendors already present that you don’t compete with, helping you understand the existing tech stack and where your solution could fit alongside it.',
};

export function queueIntro(queue) {
  if (queue === 'sam') return QUEUE_INTROS.in_market;
  return QUEUE_INTROS[queue] || '';
}

export const QUEUE_TAB_ORDER = ['priority', 'in_market', 'rest', 'adjacent'];

/** Drop the freshness disclaimer the API sometimes appends to queue descriptions. */
function cleanQueueDescription(raw) {
  return String(raw || '')
    .replace(
      /This list is rebuilt from current evidence every time it loads[,;—–-]?\s*so nothing is held back by a date\.?/gi,
      '',
    )
    .replace(/[^.]*rebuilt from current evidence[^.]*\.?/gi, '')
    .replace(/[^.]*held back by a date[^.]*\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*([.!?])\s*$/g, '$1')
    .trim();
}

/** Prefer API queue_meta; force user-facing labels (Priority, Worth a look). */
export function resolveQueueTabs(payload) {
  const fromApi = Array.isArray(payload?.queue_meta) ? payload.queue_meta : [];
  const labelFor = (key, apiLabel) => {
    if (key === 'priority') return 'Priority';
    if (key === 'in_market' || key === 'sam') return 'Worth a look';
    if (key === 'rest') return 'Rest of market';
    if (key === 'adjacent') return 'Adjacent market';
    const cleaned = String(apiLabel || '')
      .replace(/^Priority\s+now$/i, 'Priority')
      .replace(/^SAM$/i, 'In your market')
      .trim();
    return cleaned || QUEUE_META[key]?.label || String(key);
  };
  if (fromApi.length) {
    return fromApi.map((q) => {
      const key = q.queue || q.key;
      return {
        key,
        label: labelFor(key, q.label),
        description: cleanQueueDescription(q.description),
        count: q.count ?? payload?.queue_counts?.[key],
      };
    }).filter((q) => q.key);
  }
  return QUEUE_TAB_ORDER.map((key) => ({
    key,
    label: labelFor(key),
    description: '',
    count: payload?.queue_counts?.[key],
  }));
}

export function groupAdjacentByVendor(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const incumbent = getIncumbent(row);
    const fromCats = rowCategories(row)
      .map((c) => c.incumbent_vendor || c.vendor)
      .filter(Boolean);
    const vendor = incumbent.vendor
      || fromCats[0]
      || row?.incumbent_vendor
      || row?.vendor
      || row?.adjacent_vendor
      || 'Unknown vendor';
    const entry = map.get(vendor) || { vendor, count: 0, accounts: [] };
    entry.count += 1;
    entry.accounts.push(row);
    map.set(vendor, entry);
  });
  return [...map.values()].sort((a, b) => b.count - a.count || a.vendor.localeCompare(b.vendor));
}

export const RENEWAL_RISK_LEVELS = {
  confirmed: {
    key: 'confirmed',
    label: 'Confirmed risk',
    className: 'bg-[#0A1A2F] text-white',
    means: 'They have said they are unhappy with us, or their contract ends inside the horizon.',
  },
  watch: {
    key: 'watch',
    label: 'Watch',
    className: 'bg-amber-100 text-amber-900 border border-amber-200',
    means: 'There is a published failing in our product here, or a competitor has moved into a neighbouring category.',
  },
  unknown: {
    key: 'unknown',
    label: 'Not yet checked',
    className: 'bg-[#D6D3D1] text-[#0A1A2F] border border-dashed border-stone-500',
    means: 'Nobody has looked at this account. It is not the same as it being quiet.',
  },
  quiet: {
    key: 'quiet',
    label: 'Looked · nothing found',
    className: 'bg-gray-100 text-[#5C6B7A]',
    means: 'We looked and found nothing. That is a real answer, not a gap.',
  },
};

export const RENEWAL_RISK_SORT = ['confirmed', 'watch', 'unknown', 'quiet'];

export const RETENTION_ENRICH_SCOPE = ['incumbent_sentiment', 'contract_end_date'];

export function getRenewalRiskMeta(level) {
  const key = String(level || 'unknown').toLowerCase();
  return RENEWAL_RISK_LEVELS[key] || RENEWAL_RISK_LEVELS.unknown;
}

/** Ignore-list reason chips — styles are FE-owned; labels override API where needed. */
export const IGNORE_REASON_META = {
  institutional_adverse: {
    key: 'institutional_adverse',
    className: 'bg-[#0A1A2F] text-white',
  },
  financial_distress: {
    key: 'financial_distress',
    className: 'bg-amber-100 text-amber-900 border border-amber-200',
  },
  committed_to_incumbent: {
    key: 'committed_to_incumbent',
    className: 'bg-[#1C485C] text-white',
  },
  tenant_declared: {
    key: 'tenant_declared',
    label: 'User Selected',
    className: 'bg-slate-100 text-slate-700 border border-slate-200',
  },
};

export function getIgnoreReasonMeta(reason) {
  const key = String(reason || '').toLowerCase();
  return IGNORE_REASON_META[key] || {
    key,
    className: 'bg-gray-100 text-[#5C6B7A]',
  };
}

export function formatIgnoreReasonLabel(reasonOrLabel) {
  const raw = String(reasonOrLabel || '').trim();
  if (!raw) return '—';
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  const meta = IGNORE_REASON_META[key];
  if (meta?.label) return meta.label;
  // API may still send the old display string
  if (/^tenant[_\s-]?declared$/i.test(raw)) return 'User Selected';
  return raw.replace(/_/g, ' ');
}

/** One account, one chip. First matching key in this order wins. */
export const IGNORE_REASON_PRIORITY = [
  'institutional_adverse',
  'financial_distress',
  'committed_to_incumbent',
  'tenant_declared',
];

export function primaryIgnoreReason(row) {
  const keys = asList(row?.reasons)
    .map((r) => (typeof r === 'string' ? r : r?.reason))
    .map((k) => String(k || '').trim())
    .filter(Boolean);
  if (!keys.length) return null;
  const lower = keys.map((k) => k.toLowerCase());
  for (const preferred of IGNORE_REASON_PRIORITY) {
    const i = lower.indexOf(preferred);
    if (i >= 0) return keys[i];
  }
  return keys[0];
}

export function ignoreReasonChipCounts(rows) {
  const counts = {};
  for (const row of rows || []) {
    const key = primaryIgnoreReason(row);
    if (!key) continue;
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

export function rowMatchesIgnoreReason(row, reasonFilter) {
  if (!reasonFilter || reasonFilter === 'all') return true;
  return primaryIgnoreReason(row) === reasonFilter;
}

function numericSignal(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

/** Signed score printed on a motion evidence line. Never Math.abs. */
export function evidenceSignedScore(item) {
  if (!item || typeof item !== 'object') return null;
  return numericSignal(
    item.contribution ?? item.score ?? item.signal_score ?? item.persona_signal_score,
  );
}

/** Net signed score for Accounts in motion. Negative means moving away. */
export function signedMotionScore(row) {
  const evidence = Array.isArray(row?.evidence) ? row.evidence : [];
  const fromEvidence = evidence
    .map((e) => evidenceSignedScore(e))
    .filter((n) => n != null);
  if (fromEvidence.length) return fromEvidence.reduce((a, b) => a + b, 0);

  const moved = evidenceSignedScore(row?.what_moved);
  if (moved != null) return moved;

  const m12 = numericSignal(row?.momentum_12w);
  if (m12 != null) return m12;
  return numericSignal(row?.momentum_4w);
}

export function resolveMotionDirection(row) {
  const signed = signedMotionScore(row);
  if (signed != null && signed < 0) return 'under_pressure';
  if (signed != null && signed > 0) return 'toward';
  const api = String(row?.direction || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (api === 'under_pressure' || api === 'away' || api === 'moving_away') return 'under_pressure';
  if (api === 'toward' || api === 'moving_toward') return 'toward';
  return 'flat';
}

export function hasBuyingEvidence(row) {
  const b = row?.buying_evidence;
  if (b == null || b === false) return false;
  if (typeof b === 'string') return b.trim() !== '';
  if (Array.isArray(b)) return b.length > 0;
  if (typeof b === 'object') return true;
  return Boolean(b);
}

/**
 * Card / tile bucket. Sign wins: a −5 compliance update is never “Buying evidence”.
 * Buying evidence is only the label when the net score is not negative.
 */
export function motionCardBucket(row) {
  const direction = resolveMotionDirection(row);
  if (direction === 'under_pressure') return 'pressure';
  if (hasBuyingEvidence(row)) return 'buying';
  if (direction === 'toward') return 'toward';
  return 'flat';
}

/** Keep the visible list aligned with the selected badge even if the payload is mixed. */
export function filterAccountsInMotionRows(rows, direction) {
  const d = direction == null ? '' : String(direction).trim().toLowerCase();
  if (!d) return rows || [];
  return (rows || []).filter((row) => {
    if (d === 'buying') return hasBuyingEvidence(row);
    if (d === 'flat') return resolveMotionDirection(row) === 'flat';
    if (d === 'under_pressure') return resolveMotionDirection(row) === 'under_pressure';
    if (d === 'toward') return resolveMotionDirection(row) === 'toward';
    return true;
  });
}

export function motionBucketStats(rows) {
  let toward = 0;
  let pressure = 0;
  let buying = 0;
  let flat = 0;
  for (const row of rows || []) {
    const bucket = motionCardBucket(row);
    if (bucket === 'toward') toward += 1;
    else if (bucket === 'pressure') pressure += 1;
    else if (bucket === 'buying') buying += 1;
    else flat += 1;
  }
  return {
    moving_toward: toward,
    under_pressure: pressure,
    with_buying_evidence: buying,
    flat,
    total: (rows || []).length,
    bucketed: toward + pressure + buying,
  };
}

/** True when the card prints signed signal scores — 4w/12w chips would be a different set. */
export function motionHasDisplayedSignalScores(row) {
  const evidence = Array.isArray(row?.evidence) ? row.evidence : [];
  if (evidence.some((e) => evidenceSignedScore(e) != null)) return true;
  return evidenceSignedScore(row?.what_moved) != null;
}

function changeFromTo(row) {
  const from = row?.from ?? row?.from_value ?? row?.old_value ?? row?.previous ?? row?.before;
  const to = row?.to ?? row?.to_value ?? row?.new_value ?? row?.current ?? row?.after;
  return { from, to };
}

const IDENTITY_ARROW_RE = /([^\s→—–]+)\s*(?:→|->|—|–)\s*([^\s(]+)\s*(?:\(([-+]?\d+(?:\.\d+)?)\))?/;
const IDENTITY_ARROW_SUFFIX_RE = /:?\s*[^\s→—–:]+\s*(?:→|->|—|–)\s*[^\s(]+\s*(?:\(([-+]?\d+(?:\.\d+)?)\))?\s*$/;

function changeIdentityText(row) {
  return [
    row?.title,
    row?.summary,
    row?.message,
    row?.body,
    row?.detail,
    row?.account_name,
  ].filter(Boolean).join(' ');
}

function identityArrowMatch(row) {
  return changeIdentityText(row).match(IDENTITY_ARROW_RE);
}

export function isIdentityTransition(row) {
  if (!row) return false;
  const { from, to } = changeFromTo(row);
  if (from != null && from !== '' && to != null && to !== '' && String(from) === String(to)) {
    return true;
  }
  const arrow = identityArrowMatch(row);
  return Boolean(arrow && arrow[1] === arrow[2]);
}

function identityIsNumeric(row) {
  const { from, to } = changeFromTo(row);
  if (from != null && to != null && String(from) === String(to) && /^-?\d+(\.\d+)?$/.test(String(from).trim())) {
    return true;
  }
  const arrow = identityArrowMatch(row);
  return Boolean(arrow && arrow[1] === arrow[2] && /^-?\d+(\.\d+)?$/.test(arrow[1]));
}

export function isListMembershipChange(row) {
  const text = [
    row?.title,
    row?.summary,
    row?.message,
    row?.type,
    row?.kind,
    row?.event,
    row?.change_type,
  ].filter(Boolean).join(' ');
  return /\b(entered|left)\b/i.test(text);
}

/**
 * Drop identity transitions (“61 → 61 (0)”, same placement both sides).
 * Keep enter/leave Priority rows whose score did not move — the membership
 * changed even if the printed number did not.
 */
export function isNoOpMarketChange(row) {
  if (!isIdentityTransition(row)) return false;
  if (isListMembershipChange(row) && identityIsNumeric(row)) return false;
  return true;
}

/** Strip a printed identity so the row names the real event. */
export function marketChangeDisplayTitle(row) {
  const account = row?.account_name || row?.name || '';
  let title = row?.title || row?.summary || row?.message || '';
  if (isIdentityTransition(row)) {
    title = String(title).replace(IDENTITY_ARROW_SUFFIX_RE, '').trim();
  }
  if (account && title && !title.includes(account)) {
    return `${title} ${account}`.replace(/\s+/g, ' ').trim();
  }
  return title || account || 'Update';
}

export function marketChangeDisplayDetail(row) {
  const detail = row?.body || row?.detail;
  if (!detail) return null;
  const arrow = String(detail).match(IDENTITY_ARROW_RE);
  if (arrow && arrow[1] === arrow[2]) {
    const stripped = String(detail).replace(IDENTITY_ARROW_SUFFIX_RE, '').trim();
    return stripped || null;
  }
  return detail;
}

/** "Returns to your list on 20 Aug 2027" — or returns_note when date is null. Pass `t` to translate the sentence. */
export function formatIgnoreReturns(returns, returnsNote, t) {
  if (returns) {
    const d = new Date(returns);
    if (!Number.isNaN(d.getTime())) {
      const formatted = d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      if (typeof t === 'function') return t('mktReturnsOn', { date: formatted });
      return `Returns to your list on ${formatted}`;
    }
  }
  if (returnsNote) return String(returnsNote);
  return null;
}

export function sortRenewalRiskRows(rows) {
  const rank = Object.fromEntries(RENEWAL_RISK_SORT.map((k, i) => [k, i]));
  return [...(rows || [])].sort((a, b) => {
    const ra = rank[a?.risk_level] ?? rank.unknown;
    const rb = rank[b?.risk_level] ?? rank.unknown;
    if (ra !== rb) return ra - rb;
    return (Number(b?.enrolment) || 0) - (Number(a?.enrolment) || 0);
  });
}

export function renewalEvidenceHeld(payload, rows) {
  if (payload?.evidence?.with_any_reason != null) return Number(payload.evidence.with_any_reason) || 0;
  if (payload?.evidence?.accounts != null) return Number(payload.evidence.accounts) || 0;
  const list = rows || payload?.rows || [];
  return list.filter((r) => {
    if (r?.risk_level === 'confirmed' || r?.risk_level === 'watch') return true;
    return Array.isArray(r?.risk_reasons) && r.risk_reasons.length > 0;
  }).length;
}

export function unconfirmedProductCount(payload, rows) {
  if (payload?.evidence?.unconfirmed_product != null) return Number(payload.evidence.unconfirmed_product) || 0;
  const list = rows || payload?.rows || [];
  return list.filter((r) => r?.confirmed_product === false).length;
}

export function rowSourceGap(row) {
  if (row?.confirmed_product === false) return 'clients_only';
  const stack = row?.in_vendor_stack ?? row?.in_inst_vendor_stack ?? row?.sources?.vendor_stack;
  const clients = row?.in_tenant_clients ?? row?.sources?.tenant_clients;
  if (stack === true && clients === false) return 'stack_only';
  if (stack === false && clients === true) return 'clients_only';
  return null;
}

export function getQueueMeta(queue) {
  if (!queue) return { key: null, label: 'Relationship', worklist: false };
  return QUEUE_META[queue] || { key: queue, label: String(queue).replace(/_/g, ' '), worklist: false };
}

export function coverageCells(summary) {
  const c = summary?.coverage_cells || {};
  const examined = c.examined;
  const examinable = c.examinable ?? c.total;
  const unexamined = c.unexamined;
  if (examined == null && examinable == null) return null;
  return { examined, examinable, unexamined, resolved_share: c.resolved_share };
}

export const ENRICH_TOOLTIP =
  'Sends this account to the research team to fill in the gaps: who they use, how widely, when the contract ends, and whether they are happy with it. Your score will not change today. When the research comes back the account is rescored and you will see it in What changed.';

export const ENRICH_REQUEST_TOAST =
  'We’ll process your request within 48 hours and notify you when it’s ready.';

export const PLAYERS_CHART_TOOLTIP =
  'The share of institutions in your market running each vendor for this category. It is a count of institutions, not revenue. Institution shares can sum above 100% because an account can run more than one vendor.';

export const LMS_VIEW = 'lms';
export const PLAYERS_SHARE_THRESHOLD = 0.04;

function asFiniteNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isLmsCategoryKey(key) {
  return String(key || '').trim().toLowerCase() === LMS_VIEW;
}

export function categoryCellCount(cat) {
  return asFiniteNumber(cat?.total_cells) ?? asFiniteNumber(cat?.resolved_cells) ?? 0;
}

export function categoryHasCells(cat) {
  return categoryCellCount(cat) > 0;
}

export function visiblePlayerCategories(payload) {
  return (payload?.categories || []).filter(
    (c) => !isLmsCategoryKey(c.category || c.key) && categoryHasCells(c),
  );
}

export function lmsCategoryFromPayload(payload) {
  if (!payload) return null;
  const cats = payload.categories || [];
  const named = cats.find((c) => isLmsCategoryKey(c.category || c.key));
  if (named) return named;
  if (Array.isArray(payload.vendors)) {
    return payload;
  }
  if (cats.length === 1) return cats[0];
  return null;
}

export function lmsChipVisible(lmsPayload) {
  return categoryHasCells(lmsCategoryFromPayload(lmsPayload));
}

export function playerCategoryMatchesDefault(cat, def) {
  if (!cat || def == null || def === '') return false;
  const needle = String(def).trim();
  if (!needle) return false;
  const keys = [cat.category, cat.key, cat.label].filter((k) => k != null && k !== '');
  if (keys.some((k) => String(k) === needle || String(k).toLowerCase() === needle.toLowerCase())) {
    return true;
  }
  const needleLabel = formatCategoryLabel(needle);
  return keys.some((k) => formatCategoryLabel(k) === needleLabel);
}

export function resolvePlayersDefaultCategory(payload, visibleCategories, { lmsVisible } = {}) {
  const def = payload?.default_category ?? payload?.defaultCategory;
  if (def != null && def !== '') {
    if (isLmsCategoryKey(def) || formatCategoryLabel(def) === 'LMS') {
      if (lmsVisible) return LMS_VIEW;
    } else {
      const match = (visibleCategories || []).find((c) => playerCategoryMatchesDefault(c, def));
      if (match) return match.category || match.key;
    }
  }
  const flagged = (visibleCategories || []).find((c) => c.default || c.is_default);
  if (flagged) return flagged.category || flagged.key;
  if (visibleCategories?.[0]) return visibleCategories[0].category || visibleCategories[0].key;
  if (lmsVisible) return LMS_VIEW;
  return null;
}

export function categoryAccountsTotal(cat) {
  const examined = asFiniteNumber(cat?.accounts_examined);
  if (examined != null) return examined;
  return (cat?.vendors || []).reduce(
    (sum, v) => sum + vendorAccountCount(v),
    0,
  );
}

export function playersExaminedCaption(cat, t) {
  const examined = asFiniteNumber(cat?.accounts_examined);
  const inScope = asFiniteNumber(cat?.accounts_in_scope);
  if (examined == null || inScope == null) return null;
  if (typeof t === 'function') {
    return t('mktAccountsExaminedOf', {
      examined: examined.toLocaleString('en-US'),
      total: inScope.toLocaleString('en-US'),
    });
  }
  return `${examined.toLocaleString('en-US')} of ${inScope.toLocaleString('en-US')} accounts examined`;
}

export function vendorAccountCount(vendor) {
  return asFiniteNumber(vendor?.accounts)
    ?? asFiniteNumber(vendor?.account_count)
    ?? asFiniteNumber(vendor?.count)
    ?? asFiniteNumber(vendor?.n)
    ?? 0;
}

export function vendorAccountShare(vendor, total) {
  const accountShare = asFiniteNumber(vendor?.account_share);
  if (accountShare != null) return accountShare > 1 ? accountShare / 100 : accountShare;
  const hasAccountHeadcount = asFiniteNumber(vendor?.accounts) != null
    || asFiniteNumber(vendor?.account_count) != null;
  if (hasAccountHeadcount && total) return vendorAccountCount(vendor) / total;
  const cellShare = asFiniteNumber(vendor?.share);
  if (cellShare != null) return cellShare > 1 ? cellShare / 100 : cellShare;
  if (!total) return 0;
  return vendorAccountCount(vendor) / total;
}

/** Bar chart domain is 0–100. Convert a 0–1 payload so 0.34 becomes 34, not a 0.34% sliver. */
export function toPlayersBarPercents(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const max = list.reduce((m, r) => Math.max(m, Number(r?.share) || 0), 0);
  if (max > 1) return list;
  return list.map((r) => ({ ...r, share: Math.round((Number(r?.share) || 0) * 100) }));
}

export function consolidatePlayersVendors(vendors, total, threshold = PLAYERS_SHARE_THRESHOLD) {
  const kept = [];
  let otherCount = 0;
  let otherShare = 0;
  let otherVendors = 0;
  (vendors || []).forEach((v) => {
    const share = vendorAccountShare(v, total);
    if (share >= threshold) {
      kept.push({
        name: v.label || v.key,
        count: vendorAccountCount(v),
        share: Math.round(share * 100),
        kind: v.kind,
        key: v.key,
      });
    } else {
      otherCount += vendorAccountCount(v);
      otherShare += share;
      otherVendors += 1;
    }
  });
  kept.sort((a, b) => b.share - a.share);
  if (otherVendors > 0 && (otherCount > 0 || otherShare > 0)) {
    kept.push({
      name: otherVendors > 1 ? `Other (${otherVendors})` : 'Other',
      count: otherCount,
      share: otherShare > 0 ? Math.round(otherShare * 100) : (total ? Math.round((otherCount / total) * 100) : 0),
      kind: 'other',
      key: 'other',
    });
  }
  return kept;
}

export const FIT_ADD_UNSCORED_FALLBACK =
  'This account is in your Accounts, but Market had no score for it.';

/** Opportunities type filter: All / Greenfield / Displacement / Expansion. */
export const MAP_TYPE_FILTERS = [
  { key: 'all', label: 'All', types: null },
  { key: 'greenfield', label: 'Greenfield', types: ['greenfield'] },
  {
    key: 'displacement',
    label: 'Displacement',
    // Includes quiet displacement_candidate — table badge also reads “Displacement”.
    types: [
      'commercial_displacement',
      'renewal_recompete',
      'homegrown_displacement',
      'displacement_candidate',
    ],
  },
  { key: 'expansion', label: 'Expansion', types: ['expansion'] },
];

export function rowMatchesMapTypeFilter(row, filterKey) {
  if (!filterKey || filterKey === 'all') return true;
  const meta = MAP_TYPE_FILTERS.find((f) => f.key === filterKey);
  if (!meta?.types) return true;
  const resolved = resolveOpportunityType(row);
  if (meta.types.includes(resolved)) return true;
  // Fall back to category-level types when the row key is blank/sparse.
  return rowCategories(row).some((c) => meta.types.includes(canonicalizeOpportunityType(c.opportunity_type)));
}

export const VENDOR_RELATIONSHIP_META = {
  competitor: { key: 'competitor', label: 'Competitor', className: 'bg-red-100 text-red-800 border-red-200' },
  adjacent: { key: 'adjacent', label: 'Adjacent', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  ours: { key: 'ours', label: 'Ours', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  non_commercial: { key: 'non_commercial', label: 'In-house', className: 'bg-gray-200 text-gray-600 border-gray-300' },
};

export function getVendorRelationshipMeta(value) {
  if (!value || value === 'none') return null;
  return VENDOR_RELATIONSHIP_META[value] || {
    key: value,
    label: String(value).replace(/_/g, ' '),
    className: 'bg-gray-100 text-gray-700 border-gray-200',
  };
}

export function mixSliceColor(type) {
  const key = canonicalizeOpportunityType(type);
  const colors = {
    commercial_displacement: '#0A1A2F',
    displacement_candidate: '#C4785A',
    renewal_recompete: '#9A3412',
    homegrown_displacement: '#1C485C',
    greenfield: '#94A3B8',
    expansion: '#4338CA',
    coexistence_adoption: '#64748B',
    addon: '#059669',
  };
  return colors[key] || '#CBD5E1';
}

export function vendorKindColor(kind) {
  if (kind === 'competitor') return '#DC2626';
  if (kind === 'ours') return '#059669';
  if (kind === 'adjacent') return '#64748B';
  if (kind === 'non_commercial') return '#9CA3AF';
  return '#94A3B8';
}

function pickCount(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'object') {
      const n = Number(v.count ?? v.value ?? v.total);
      if (!Number.isNaN(n)) return n;
    }
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

/** Funnel is an OBJECT. Prefer it over funnel_stages — stages are not country-scoped. */
export function summariseHeadline(summary) {
  const stages = Array.isArray(summary?.funnel_stages) ? summary.funnel_stages : [];
  const byStage = Object.fromEntries(stages.filter((s) => s?.key).map((s) => [s.key, s]));
  const f = summary?.funnel && !Array.isArray(summary.funnel) ? summary.funnel : {};

  const countFor = (keys) => {
    for (const k of keys) {
      const fromObj = pickCount(f[k]);
      if (fromObj != null) return fromObj;
      const fromStage = pickCount(byStage[k]?.count);
      if (fromStage != null) return fromStage;
    }
    return null;
  };

  return [
    {
      key: 'known',
      label: 'Total market',
      count: countFor(['known']),
      valueKey: 'tam',
      tooltip: HEADLINE_TOOLTIPS.known,
    },
    {
      key: 'addressable',
      label: byStage.addressable?.label || 'Addressable',
      count: countFor(['addressable']),
      valueKey: 'addressable',
      tooltip: HEADLINE_TOOLTIPS.addressable,
    },
    {
      key: 'in_market',
      label: 'In your market',
      count: countFor(['in_market', 'sam']),
      valueKey: 'sam',
      tooltip: HEADLINE_TOOLTIPS.in_market,
    },
    {
      key: 'priority',
      label: 'Priority',
      count: countFor(['priority', 'priority_now']),
      valueKey: 'priority',
      tooltip: HEADLINE_TOOLTIPS.priority,
    },
  ].map((item) => {
    const v = summary?.value || {};
    const amount = v[item.valueKey];
    const annual = v[`${item.valueKey}_annual`];
    return {
      ...item,
      amount,
      annual,
      currency: v.currency || 'USD',
      basis: v.basis,
      termYears: v.term_years,
      provisionalShare: v.provisional_share,
    };
  });
}

export function rowCategories(row) {
  return asList(row?.categories).map((c) => {
    if (typeof c === 'string') return { category: c };
    return c;
  }).filter((c) => c?.category);
}

export function formatScoreBreakdown(breakdown, row) {
  const b = breakdown || row?.score_breakdown;
  if (!b) return null;
  if (b.plain?.sentence) return b.plain.sentence;
  const structural = b.structural ?? b.structural_score ?? row?.structural_score;
  const live = b.live ?? b.live_score ?? row?.live_score;
  const conf = b.confidence_factor ?? row?.confidence_factor;
  const score = b.market_score ?? row?.market_score;
  if (structural == null && live == null) return null;
  const cert = conf != null && !Number.isNaN(Number(conf)) ? `${Math.round(Number(conf) * 100)}%` : null;
  const parts = [];
  if (structural != null) parts.push(`Fit ${structural}`);
  if (live != null) parts.push(`Signals ${live}`);
  if (cert) parts.push(`Confidence ${cert}`);
  if (!parts.length) return null;
  return `${parts.join(' · ')}${score != null ? ` → ${score}` : ''}`;
}

/**
 * The same three numbers as formatScoreBreakdown, but as parts.
 *
 * The string version cannot carry a tooltip: by the time it reads
 * "Fit 83 · Signals 90 · Confidence 94" there is nothing left to hang an
 * explanation on. Fit and Signals are NOT interchangeable — a low Signals means
 * quiet, not weak — and that is exactly what a rep cannot infer from the label.
 */
export function scoreBreakdownParts(breakdown, row) {
  const b = breakdown || row?.score_breakdown;
  if (!b) return [];
  const plain = b.plain || {};
  const pct = (v) => (v != null && !Number.isNaN(Number(v)) ? Math.round(Number(v) * 100) : null);
  const out = [];

  const fitValue = plain.fit?.value ?? b.structural ?? b.structural_score ?? row?.structural_score;
  if (fitValue != null) {
    out.push({ key: 'fit', label: 'Fit', value: fitValue, means: plain.fit?.means || '' });
  }

  const sigValue = plain.activity?.value ?? b.live ?? b.live_score ?? row?.live_score;
  if (sigValue != null) {
    out.push({ key: 'activity', label: 'Signals', value: sigValue, means: plain.activity?.means || '' });
  }

  const confValue = plain.confidence?.value ?? pct(b.confidence_factor ?? row?.confidence_factor);
  if (confValue != null) {
    out.push({
      key: 'confidence',
      label: 'Confidence',
      value: confValue,
      means: plain.confidence?.means || '',
      reason: b.confidence_reason || null,
    });
  }
  return out;
}

export function scorePlainChips(breakdown, row) {
  const plain = (breakdown || row?.score_breakdown)?.plain;
  const chips = [];
  if (plain && typeof plain === 'object') {
    ['fit', 'activity', 'confidence'].forEach((key) => {
      const p = plain[key];
      if (!p) return;
      if (p.value == null && !p.means) return;
      chips.push({
        key,
        label: key === 'activity' ? 'Signals' : (p.label || key),
        value: p.value,
        means: p.means,
        ...(key === 'confidence' ? { reason: (breakdown || row?.score_breakdown)?.confidence_reason || null } : {}),
      });
    });
  }
  const mult = Number(
    breakdown?.event_multiplier
    ?? row?.score_breakdown?.event_multiplier
    ?? row?.event_multiplier,
  );
  if (!Number.isNaN(mult) && mult > 1) {
    chips.push({
      key: 'event_multiplier',
      label: 'Rare event',
      value: `×${mult}`,
      means: 'A rare qualifying event lifted the live activity axis on this score.',
    });
  }
  return chips;
}

export function formatContractScope(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'unknown') {
    return {
      display: '—',
      unknown: true,
      hint: 'Nobody has established this yet. It does not mean the deployment is small, so we leave it out of the score rather than marking it down.',
    };
  }
  const pretty = raw.replace(/_/g, ' ');
  return {
    display: pretty.charAt(0).toUpperCase() + pretty.slice(1),
    unknown: false,
    hint: COLUMN_TOOLTIPS.contract_scope,
  };
}

export function rubricWeightLabel(key) {
  const k = String(key || '').toLowerCase();
  if (k.includes('structur') || k === 'fit' || k.includes('profile')) {
    return {
      label: 'Fit',
      hint: 'How well an institution suits what you sell: what they use today and how replaceable it is, their size, and how ready they look to change. Turn it up to favour the right-shaped accounts even when nothing is happening there yet.',
    };
  }
  if (k.includes('live') || k === 'activity' || k.includes('evidence')) {
    return {
      label: 'Buying signals',
      hint: 'Signs they are actually moving: a stated problem with what they use now, plus procurement, budget, a renewal coming up or a new award. Turn it up to lead with what is happening rather than who looks right on paper.',
    };
  }
  return { label: String(key || '').replace(/_/g, ' '), hint: '' };
}

const CATEGORY_DISPLAY_LABELS = {
  course_evaluation: 'Feedback & Evaluation',
  'course evaluation': 'Feedback & Evaluation',
  course_eval: 'Feedback & Evaluation',
  online_exams: 'Online Exams',
  'online exams': 'Online Exams',
  lms: 'LMS',
  sis: 'SIS',
  academic_integrity: 'Academic Integrity',
  'academic integrity': 'Academic Integrity',
  polling: 'Polling & Engagement',
  polling_engagement: 'Polling & Engagement',
  'polling & engagement': 'Polling & Engagement',
  library_systems: 'Library & Discovery Services / Reading List Systems',
  digital_course_content: 'Digital Course Content & Inclusive Access',
  reading_literacy: 'Reading & Literacy Platforms',
};

/** Strip tenant / SAM suffixes so lens names read as the category alone. */
export function lensDisplayName(nameOrKey) {
  if (!nameOrKey) return '';
  let raw = String(nameOrKey).trim();
  raw = raw.replace(/\s*·?\s*SAM\s+\d+\s*$/i, '');
  // "evasys — course evaluation" / "tenant - course_evaluation"
  const parts = raw.split(/\s*[—–]\s*|\s+-\s+/);
  if (parts.length > 1) raw = parts[parts.length - 1].trim();
  return formatCategoryLabel(raw);
}

export function formatCategoryLabel(key) {
  if (!key) return '—';
  let raw = String(key).trim();
  const parts = raw.split(/\s*[—–]\s*|\s+-\s+/);
  if (parts.length > 1) raw = parts[parts.length - 1].trim();

  const slug = raw.toLowerCase().replace(/\s+/g, '_');
  const canonical = CATEGORY_KEY_ALIASES[slug] || slug;

  const mapped = CATEGORY_DISPLAY_LABELS[raw]
    || CATEGORY_DISPLAY_LABELS[raw.toLowerCase()]
    || CATEGORY_DISPLAY_LABELS[slug]
    || CATEGORY_KEY_TO_OFFICIAL[canonical];
  if (mapped) return mapped;
  return raw.replace(/_/g, ' ');
}

export function uniqueCategoryKeys(rows, extra = []) {
  const set = new Set(extra.filter(Boolean));
  (rows || []).forEach((row) => {
    rowCategories(row).forEach((c) => { if (c.category) set.add(c.category); });
  });
  return [...set];
}

export function rowMatchesCategory(row, category) {
  if (!category || category === 'all') return true;
  return rowCategories(row).some((c) => c.category === category);
}

export function extractThresholds(prefs) {
  const t = prefs?.thresholds || prefs?.lens?.thresholds || {};
  return {
    priority_score: t.priority_score ?? t.priority ?? t.min_score ?? '',
    min_evidence: t.min_evidence ?? t.minimum_evidence ?? t.confidence_floor ?? '',
  };
}

export function countriesCoveredEmptyFromSummary(summary) {
  const cov = summary?.coverage || {};
  const raw = cov.countries_covered_empty || cov.countries_empty || cov.countries_loaded_empty || [];
  return asList(raw).map((c) => (typeof c === 'string' ? c : c?.name || c?.country)).filter(Boolean);
}

export function countriesNotCoveredFromSummary(summary) {
  const cov = summary?.coverage || {};
  const raw = cov.countries_not_covered || [];
  return asList(raw).map((c) => (typeof c === 'string' ? c : c?.name || c?.country)).filter(Boolean);
}

export function coverageStateForCountry(summary, country) {
  const rows = asList(summary?.coverage?.by_country);
  const hit = rows.find((r) => {
    const name = typeof r === 'string' ? r : r?.country;
    return String(name || '').toLowerCase() === String(country || '').toLowerCase();
  });
  if (hit?.state) return hit.state;
  return null;
}

export function extractCompetitorSet(prefs) {
  const raw =
    prefs?.lens?.competitor_set
    || prefs?.competitor_set
    || prefs?.competitors
    || prefs?.lens?.competitors
    || [];
  return asList(raw).map((v) => (typeof v === 'string' ? v : v?.name || v?.vendor || v?.key)).filter(Boolean);
}

export function extractSamThresholds(prefs) {
  const s = prefs?.sam || prefs?.thresholds || {};
  return {
    sam_min_pain: s.sam_min_pain ?? prefs?.sam_min_pain ?? 3,
    sam_min_confidence: s.sam_min_confidence ?? prefs?.sam_min_confidence ?? 'M',
    freshness_floor: s.freshness_floor ?? prefs?.freshness_floor ?? '',
    score_threshold: s.score_threshold ?? prefs?.score_threshold ?? s.priority_score ?? 60,
  };
}

export function extractDisqualifiers(prefs) {
  const d = prefs?.disqualifiers || prefs?.lens?.disqualifiers || [];
  const fromList = Array.isArray(d)
    ? Object.fromEntries(d.map((item) => {
      const key = item.rule || item.type || item.key;
      const enabled = item.enabled !== false;
      const value = item.value ?? item.values ?? (typeof item.enabled === 'boolean' && item.enabled);
      return [key, { enabled, value }];
    }))
    : Object.fromEntries(Object.entries(d || {}).map(([k, v]) => {
      if (v && typeof v === 'object' && !Array.isArray(v) && ('enabled' in v || 'value' in v)) {
        return [k, { enabled: v.enabled !== false, value: v.value }];
      }
      return [k, { enabled: Boolean(v) || (Array.isArray(v) && v.length > 0) || (typeof v === 'number'), value: v }];
    }));
  const val = (key, fallback = '') => {
    const item = fromList[key];
    if (!item) return fallback;
    return item.value === undefined || item.value === null ? fallback : item.value;
  };
  const on = (key) => Boolean(fromList[key]?.enabled);
  return {
    is_current_vendor: on('is_current_vendor'),
    enrolment_below: val('enrolment_below', val('min_enrolment', '')),
    enrolment_above: val('enrolment_above', val('max_enrolment', '')),
    sector_not_in: asList(val('sector_not_in', val('sector_exclude', []))),
    country_not_in: asList(val('country_not_in', val('country_exclude', []))),
    financial_band_below: val('financial_band_below', ''),
    ai_readiness_below: val('ai_readiness_below', ''),
    competitor_locked: asList(val('competitor_locked', val('vendor_exclude', []))),
    manual_exclusion: asList(val('manual_exclusion', [])),
  };
}

export const OPPORTUNITIES_PAGE_SIZE = 50;

/** Postgres daterange e.g. ["2026-08-19","2027-06-30") */
export function parseDateRange(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && (raw.start || raw.lower || raw.from)) {
    return {
      start: raw.start || raw.lower || raw.from || null,
      end: raw.end || raw.upper || raw.to || null,
    };
  }
  const s = String(raw).trim();
  const m = s.match(/^[[(]\s*"?([^",\]]*)"?\s*,\s*"?([^")\]]*)"?\s*[\])]$/);
  if (!m) return { start: s, end: null, raw: s };
  return {
    start: m[1] || null,
    end: m[2] || null,
    raw: s,
  };
}

export function formatDateRange(raw) {
  const parsed = parseDateRange(raw);
  if (!parsed) return null;
  const fmt = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const a = fmt(parsed.start);
  const b = fmt(parsed.end);
  if (a && b) return `${a} – ${b}`;
  return a || b || null;
}

/**
 * A renewal date is a POINT, not a span — so render only the end.
 *
 * `tenant_opportunities.renewal_window` is built in marketIntel.ts as
 * `{ from: TODAY, to: contract_end_date }`. The `from` is literally the moment the score was
 * computed and carries no information about the renewal; the only fact in the range is the end date,
 * which is a PUBLISHED contract end or an H/M predicted expiry. Rendering it through
 * `formatDateRange` printed "4 Oct 2026 – 27 Dec 2026" and implied an uncertainty the data does not
 * have. Tiago, 2026-10-04: "sometimes the renewal date is a hard date and shouldnt be a range".
 *
 * Returns null when there is no end date, so the caller shows an em dash — "renewal window should be
 * empty if there is no predicted expiry".
 */
export function formatRenewalDate(raw) {
  const parsed = parseDateRange(raw);
  const end = parsed?.end;
  if (!end) return null;
  const dt = new Date(end);
  if (Number.isNaN(dt.getTime())) return String(end);
  /* 🔴 THE UPPER BOUND IS EXCLUSIVE, SO THE RAW END IS ONE DAY LATE.
   * Postgres canonicalises a `daterange` to `[inclusive,exclusive)`, and `parseDateRange` returns the
   * bound strings untouched. Checked against the source dates rather than assumed:
   *     Paraná  range [2026-10-04,2026-12-28)  contract_end_date 2026-12-27
   *     Tokyo   range [2026-10-04,2027-04-01)  contract_end_date 2027-03-31
   * So `formatDateRange` has always rendered this date a day late. Decrement when the bound is
   * exclusive — which is every row, because that is the canonical form. */
  const exclusive = typeof raw === 'string' && raw.trim().endsWith(')');
  if (exclusive) dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatCurrency(amount, currency = 'GBP') {
  if (amount == null || amount === '' || Number.isNaN(Number(amount))) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'GBP',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency} ${Math.round(Number(amount)).toLocaleString()}`;
  }
}

export function formatCompactMoney(amount, currency = 'USD') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const abs = Math.abs(n);
  let symbol = '';
  try {
    symbol = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value || '';
  } catch {
    symbol = currency === 'GBP' ? '£' : '$';
  }
  const trim = (v) => String(v >= 10 || v <= -10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, '');
  if (abs >= 1e9) return `${symbol}${trim(n / 1e9)}B`;
  if (abs >= 1e6) return `${symbol}${trim(n / 1e6)}M`;
  if (abs >= 1e3) return `${symbol}${trim(n / 1e3)}k`;
  return formatCurrency(n, currency);
}

export function firstEventWhen(raw) {
  if (raw == null || raw === '') return null;
  return String(raw).split('|')[0].trim() || null;
}

export function formatEventWhen(raw) {
  const first = firstEventWhen(raw);
  if (!first) return null;
  const iso = first.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return first;
}

export function activityEvents(row) {
  const src = row?.activity || {};
  const events = asList(src.events);
  return [...events].sort((a, b) => {
    // Never lead with stale evidence — fresh first, then on-target, then date.
    const as = a?.stale ? 1 : 0;
    const bs = b?.stale ? 1 : 0;
    if (as !== bs) return as - bs;
    const at = a?.about_incumbent && a?.in_lens_category ? 0 : 1;
    const bt = b?.about_incumbent && b?.in_lens_category ? 0 : 1;
    if (at !== bt) return at - bt;
    const ar = Number(a?.recency);
    const br = Number(b?.recency);
    if (!Number.isNaN(ar) && !Number.isNaN(br) && ar !== br) return br - ar;
    return String(firstEventWhen(b?.when) || '').localeCompare(String(firstEventWhen(a?.when) || ''));
  });
}

export { formatEnrolment } from './enrolmentDisplay';

export function formatMomentum(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  if (v > 0) return `+${v}`;
  return String(v);
}

export function formatConfidence(factor) {
  if (factor == null || factor === '') return null;
  const n = Number(factor);
  if (Number.isNaN(n)) return null;
  return formatEvidenceLabel(n);
}

/** Certainty framing — never quality, never a red/low-score badge. */
export function formatEvidenceLabel(factor) {
  const n = Number(factor);
  if (Number.isNaN(n)) return null;
  if (n >= 0.95) return 'Strong';
  if (n >= 0.82) return 'Moderate';
  return 'Limited';
}

export function asList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value == null || value === '') return [];
  return [value];
}

export function unwrapRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.opportunities)) return payload.opportunities;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
}

export function unwrapCount(payload, rows) {
  // Prefer `total`. `count` is page length on some endpoints (e.g. /api/entities)
  // and must not be treated as a result-set size. Do not reorder this fallback.
  const n = payload?.total ?? payload?.count ?? payload?.total_count;
  if (typeof n === 'number') return n;
  return rows.length;
}

function countFromUnknown(obj) {
  if (obj == null) return [];
  const items = [];
  if (Array.isArray(obj)) {
    obj.forEach((item) => {
      if (item && typeof item === 'object') {
        items.push({
          key: canonicalizeOpportunityType(item.type || item.key || item.stage || item.name),
          count: Number(item.count ?? item.value ?? 0) || 0,
        });
      }
    });
  } else if (typeof obj === 'object') {
    Object.entries(obj).forEach(([key, v]) => {
      if (!(typeof v === 'number' || (v && typeof v === 'object'))) return;
      items.push({
        key: canonicalizeOpportunityType(key),
        count: typeof v === 'number' ? v : Number(v?.count ?? 0) || 0,
      });
    });
  }
  const merged = {};
  items.forEach((item) => {
    if (!item.key) return;
    merged[item.key] = (merged[item.key] || 0) + item.count;
  });
  return Object.entries(merged).map(([key, count]) => ({
    key,
    label: getOpportunityTypeMeta(key).label,
    count,
  }));
}

export function summariseMixByQueue(summary) {
  const raw = summary?.mix_by_queue || {};
  const tones = {
    priority: 'go',
    in_market: 'watch',
    rest: 'hold',
    adjacent: 'hold',
  };
  return QUEUE_TAB_ORDER.map((key) => {
    const bucket = raw[key] || {};
    return {
      key,
      label: QUEUE_META[key]?.label || key,
      count: Number(bucket.accounts ?? bucket.count ?? 0) || 0,
      types: bucket.types || {},
      tone: tones[key] || 'hold',
    };
  });
}

export function summariseMix(summary, opportunityRows = []) {
  const fromSummary = countFromUnknown(summary?.mix || summary?.opportunity_mix || summary?.types);
  if (fromSummary.length) return fromSummary;
  const counts = {};
  opportunityRows.forEach((row) => {
    const k = resolveOpportunityType(row) || 'unknown';
    counts[k] = (counts[k] || 0) + 1;
  });
  return Object.entries(counts).map(([key, count]) => ({
    key,
    label: getOpportunityTypeMeta(key).label,
    count,
  }));
}

export function summariseMixGrouped(summary, opportunityRows = []) {
  const mix = summariseMix(summary, opportunityRows).filter((m) => m.key !== 'monitor');
  const byKey = Object.fromEntries(mix.map((m) => [m.key, m.count]));
  const count = (...keys) => keys.reduce((s, k) => s + (byKey[k] || 0), 0);
  return [
    {
      key: 'commercial_displacement',
      label: 'Displacement',
      count: count('commercial_displacement', 'displacement_candidate', 'homegrown_displacement'),
      tone: 'go',
    },
    {
      key: 'renewal_recompete',
      label: 'Renewal open',
      count: count('renewal_recompete'),
      tone: 'go',
    },
    {
      key: 'expansion',
      label: 'Expansion',
      count: count('expansion'),
      tone: 'watch',
    },
    {
      key: 'greenfield',
      label: 'Greenfield',
      count: count('greenfield'),
      tone: 'watch',
    },
    {
      key: 'addon',
      label: 'Existing customer',
      count: count('addon'),
      tone: 'hold',
    },
    {
      key: 'coexistence_adoption',
      label: 'Adjacent vendor',
      count: count('coexistence_adoption'),
      tone: 'hold',
    },
  ].filter((g) => g.count > 0);
}

export function summariseFunnel(summary, mix) {
  const fromSummary = countFromUnknown(summary?.funnel);
  if (fromSummary.length) return fromSummary.slice(0, 4);
  const byKey = Object.fromEntries((mix || []).map((m) => [m.key, m.count]));
  return [
    { key: 'in_play', label: 'In play', count: (byKey.commercial_displacement || 0) + (byKey.renewal_recompete || 0) },
    { key: 'candidate', label: 'Candidates', count: byKey.displacement_candidate || 0 },
    { key: 'customer', label: 'Existing / expansion', count: (byKey.expansion || 0) + (byKey.addon || 0) },
    { key: 'other', label: 'Greenfield / adjacent', count: (byKey.greenfield || 0) + (byKey.homegrown_displacement || 0) + (byKey.coexistence_adoption || 0) },
  ];
}

export function countriesLoadedFromSummary(summary) {
  const cov = summary?.coverage || {};
  const raw =
    cov.countries_loaded ||
    cov.loaded_countries ||
    summary?.countries_loaded ||
    [];
  return asList(raw).map((c) => (typeof c === 'string' ? c : c?.name || c?.country)).filter(Boolean);
}

export function countriesEntitledFromSummary(summary) {
  const cov = summary?.coverage || {};
  const raw = cov.countries_entitled || cov.entitled_countries || [];
  return asList(raw).map((c) => (typeof c === 'string' ? c : c?.name || c?.country)).filter(Boolean);
}

export function entitlementCountries(prefs) {
  const ent = prefs?.entitlement || {};
  const raw = ent.countries || ent.country || ent.markets || [];
  return asList(raw).map((c) => (typeof c === 'string' ? c : c?.name || c?.country)).filter(Boolean);
}

export function entitlementCategories(prefs) {
  const ent = prefs?.entitlement || {};
  const raw = ent.categories || ent.category || ent.lenses || [];
  return asList(raw).map((c) => (typeof c === 'string' ? c : c?.id || c?.key || c?.name)).filter(Boolean);
}

/** Same options as the Market header product-category (lens) switcher. */
export function productCategoryOptions(lenses = [], entitlementCats = []) {
  const seen = new Set();
  const out = [];
  (lenses || []).forEach((l) => {
    const value = l?.category || l?.lens_key;
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push({ value, label: lensDisplayName(l.lens_name || l.lens_key || value) });
  });
  (entitlementCats || []).forEach((c) => {
    if (!c || seen.has(c)) return;
    seen.add(c);
    out.push({ value: c, label: formatCategoryLabel(c) });
  });
  return out;
}

export function currentLens(prefs) {
  const lens = prefs?.lens;
  if (!lens) return { category: null, countries: [] };
  if (typeof lens === 'string') return { category: lens, countries: [] };
  return {
    category: lens.category || lens.category_key || lens.id || null,
    countries: asList(lens.countries || lens.country).map((c) =>
      typeof c === 'string' ? c : c?.name || c?.country,
    ).filter(Boolean),
  };
}

export function extractRubricWeights(prefs) {
  const versions = prefs?.rubric_versions || prefs?.versions || prefs?.history || [];
  const raw =
    prefs?.weights ||
    prefs?.rubric?.weights ||
    versions[0]?.weights ||
    null;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (item && typeof item === 'object') {
          const key = item.key || item.name || item.factor || item.id;
          const value = Number(item.weight ?? item.value ?? item.w);
          if (!key || Number.isNaN(value)) return null;
          return { key, value };
        }
        return null;
      })
      .filter(Boolean);
  }
  if (typeof raw === 'object') {
    return Object.entries(raw)
      .filter(([, v]) => typeof v === 'number' || !Number.isNaN(Number(v)))
      .map(([key, v]) => ({ key, value: Number(v) }));
  }
  return [];
}

export function weightsSum(weights) {
  return (weights || []).reduce((s, w) => s + (Number(w.value) || 0), 0);
}

export function weightsToPayload(weights) {
  const obj = {};
  (weights || []).forEach((w) => {
    obj[w.key] = Number(w.value);
  });
  return obj;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/** Keep rubric weights summing to 1.0 when one weight is edited. */
export function redistributeRubricWeights(weights, index, rawNext) {
  const list = (weights || []).map((w) => ({ ...w, value: Number(w.value) || 0 }));
  if (!list.length) return [];
  let nextVal = Number(rawNext);
  if (Number.isNaN(nextVal)) nextVal = 0;
  nextVal = Math.min(1, Math.max(0, round2(nextVal)));
  if (list.length === 1) return [{ ...list[0], value: 1 }];
  const others = list.map((_, i) => i).filter((i) => i !== index);
  const othersSum = others.reduce((s, i) => s + list[i].value, 0);
  const remaining = round2(1 - nextVal);
  list[index].value = nextVal;
  if (othersSum <= 0) {
    const each = round2(remaining / others.length);
    others.forEach((i, k) => {
      list[i].value = k === others.length - 1 ? round2(remaining - each * (others.length - 1)) : each;
    });
  } else {
    let allocated = 0;
    others.forEach((i, k) => {
      if (k === others.length - 1) {
        list[i].value = round2(remaining - allocated);
      } else {
        const v = round2((remaining * list[i].value) / othersSum);
        list[i].value = v;
        allocated += v;
      }
    });
  }
  return list;
}

export function hasPricingValue(value) {
  if (value == null) return false;
  const n = value.total ?? value.estimated_value ?? value.sum;
  return n != null && n !== '' && !Number.isNaN(Number(n));
}
